import React, { createContext, useContext, useRef, useState, useCallback, useEffect } from 'react';

const EscHandlerContext = createContext(null);

export const EscHandlerProvider = ({ children }) => {
  // Stack of handlers: [{ id, handler }, ...]
  // Latest registered handler is at index 0 (top of stack)
  const handlersRef = useRef([]);
  // Current position in the stack (which handler should execute next)
  const currentPositionRef = useRef(0);
  // Force re-render when handlers change (for debugging/observability)
  const [, setVersion] = useState(0);

  // Register a new handler (adds to top of stack)
  const registerHandler = useCallback((id, handler) => {
    // Remove existing handler with same id if present
    handlersRef.current = handlersRef.current.filter(h => h.id !== id);
    // Add new handler to top of stack
    handlersRef.current.unshift({ id, handler });
    // Reset position to top (new handler gets priority)
    currentPositionRef.current = 0;
    setVersion(v => v + 1);
  }, []);

  // Unregister a handler (removes from stack)
  const unregisterHandler = useCallback((id) => {
    const index = handlersRef.current.findIndex(h => h.id === id);
    if (index === -1) return;

    handlersRef.current = handlersRef.current.filter(h => h.id !== id);
    
    // Adjust current position if needed
    if (index < currentPositionRef.current) {
      // Removed handler was before current position, decrement position
      currentPositionRef.current--;
    } else if (index === currentPositionRef.current && currentPositionRef.current >= handlersRef.current.length) {
      // Removed handler was at current position and position is now invalid
      currentPositionRef.current = 0;
    }
    
    // Ensure position is valid
    if (currentPositionRef.current >= handlersRef.current.length) {
      currentPositionRef.current = 0;
    }
    
    setVersion(v => v + 1);
  }, []);

  // Handle ESC key press
  const handleEscKey = useCallback((event) => {
    if (handlersRef.current.length === 0) {
      return;
    }

    // Ensure position is valid
    if (currentPositionRef.current >= handlersRef.current.length) {
      currentPositionRef.current = 0;
    }

    // Execute handler at current position
    const handler = handlersRef.current[currentPositionRef.current];
    if (handler && handler.handler) {
      handler.handler();
    }

    // Move to next handler
    currentPositionRef.current++;
    
    // If we've reached the end, reset to top
    if (currentPositionRef.current >= handlersRef.current.length) {
      currentPositionRef.current = 0;
    }
  }, []);

  // Set up global ESC key listener
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' || event.keyCode === 27) {
        event.preventDefault();
        handleEscKey(event);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleEscKey]);

  const value = {
    registerHandler,
    unregisterHandler,
  };

  return (
    <EscHandlerContext.Provider value={value}>
      {children}
    </EscHandlerContext.Provider>
  );
};

export const useEscHandlerContext = () => {
  const context = useContext(EscHandlerContext);
  if (!context) {
    throw new Error('useEscHandlerContext must be used within EscHandlerProvider');
  }
  return context;
};

